import { Component, EventEmitter, Input, Output, OnInit, Optional} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';

import { AjaxService } from '../../../../../services/ajax.service';
import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AutoCompleteModule } from 'primeng/autocomplete';

import { SelectItem } from '../../../../../models/selected-item.model';

import { COMP_MC6111, COMP_MC } from '../../../../../services/account-api.constants';

// Interface for a MoveCode6111 entry
export interface MoveCode6111 {
    id: string;
    recId: string;
    expenseCode: string;
    category: SelectItem | null;
    categoryId?: number;
    moveCode: SelectItem | null;
    customerMoveCodeRecID?: number;
    description: string;
    isActive: number;
    isApproved: number;
}

@Component({
    selector: 'app-move-code6111',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        InputTextarea,
        ButtonModule,
        ToastModule,
        TableModule,
        TagModule,
        RadioButtonModule,
        AutoCompleteModule
    ],
    templateUrl: './move-code6111.component.html',
    styleUrls: ['./move-code6111.component.scss'],
    providers: [MessageService]
})
export class MoveCode6111Component implements OnInit {
    // Input: The list of move codes (if modifying)
    @Input() moveCode6111: MoveCode6111[] = [];
    // Input: Boolean to indicate if default codes are used
    @Input() useDefaultMoveCode6111: boolean = true;

    @Input() customerRecId!: number;

    // Outputs to the parent component
    @Output() moveCode6111Change = new EventEmitter<MoveCode6111[]>();
    @Output() useDefaultMoveCode6111Change = new EventEmitter<boolean>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

    readonly EXPENSE_CODE_MAX_LENGTH = 10;
    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;
    newEntry: MoveCode6111 = {
        id: '',
        recId : '',
        expenseCode: '',
        category: null,
        categoryId: undefined,
        moveCode: null,
        customerMoveCodeRecID: undefined,
        description: '',
        isActive: 1,      // default active
        isApproved: 1     // default approved
    };

    // Model for the radio button choice (true for default, false for modify)
    selectedCodeOption: boolean = true;

    // Predefined category options
    categoryOptions: SelectItem[] = [];
    filteredCategories: SelectItem[] = [];

    // Predefined move code options
    moveCodeOptions: SelectItem[] = [];
    filteredMoveCodes: SelectItem[] = [];

    constructor(private messageService: MessageService,
                public translateServ :TranslateService,
                public dirServ : DirectionService,
                private ajaxService : AjaxService,
                private viewScopeService: ViewScopeService,
                @Optional() public ref: DynamicDialogRef,
                @Optional() public config: DynamicDialogConfig,
                @Optional() private maintainAccountService: MaintainAccountService) {}

    ngOnInit(): void
    {
        // First, handle the dialog data and component state
        if (this.config && this.config.data)
        {
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry)
            {
                // In edit mode, store the entry temporarily
                this.moveCode6111 = [this.config.data.nameEntry];
            }
            else
            {
                this.moveCode6111 = [];
            }
            this.useDefaultMoveCode6111 = false;
            this.selectedCodeOption = false;
        }
        else
        {
            this.isDialogMode = false;
            this.isCreate = true;
            this.selectedCodeOption = this.useDefaultMoveCode6111;
            this.resetNewEntry();
        }

        // Always load categories and move codes after initializing the component state
        this.loadCategories();
        this.loadMoveCodes();
    }

    private generateUniqueId(): string {
        return 'mc6111_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void {
        this.newEntry = {
            id: this.generateUniqueId(),
            expenseCode: '',
            recId : '',
            categoryId: undefined,
            category: null,
            moveCode: null,
            customerMoveCodeRecID: undefined,
            description: '',
            isActive: 1,
            isApproved: 1
        };
    }

    onCodeOptionChange(value: boolean): void
    {
        this.selectedCodeOption = value;
        this.useDefaultMoveCode6111Change.emit(value);

        if (value) {
            this.moveCode6111 = [];
            this.emitChanges();
        }
    }

    searchCategories(event: any): void
    {
        const query = event.query?.toLowerCase().trim() || '';

        if (!query)
        {
            this.filteredCategories = [...this.categoryOptions];
        }
        else {
            this.filteredCategories = this.categoryOptions.filter(option =>
                option.label.toLowerCase().includes(query)
            );
        }
    }

    addEntry(): void {
        if (!this.newEntry.expenseCode.trim()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Expense Code is required.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.expenseCode.trim().length > this.EXPENSE_CODE_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Expense Code cannot exceed ${this.EXPENSE_CODE_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }
        if (!this.newEntry.category) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Category is required.',
                life: 3000
            });
            return;
        }
        if (!this.newEntry.moveCode) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Move Code is required.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        // Check for duplicate expense codes
        const isDuplicate = this.moveCode6111.some(
            code => code.expenseCode.toLowerCase() === this.newEntry.expenseCode.trim().toLowerCase()
        );

        if (isDuplicate) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Duplicate Entry',
                detail: 'An expense code with this type already exists.',
                life: 3000
            });
            return;
        }

        this.newEntry.categoryId = +this.newEntry.category.code;
        this.newEntry.customerMoveCodeRecID = +this.newEntry.moveCode.code;

        this.moveCode6111.push({ ...this.newEntry });
        this.emitChanges();

        this.resetNewEntry();

        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New move code added.',
            life: 1500
        });
    }

    private findCategoryCodeByName(name: string): string
    {
        const match = this.filteredCategories.find(cat => cat.label === name);
        return match ? match.code.toString() : "-1";
    }

    removeEntry(entryToRemove: MoveCode6111): void {
        this.moveCode6111 = this.moveCode6111.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Move code removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.moveCode6111Change.emit(this.moveCode6111);
    }

    getRemainingChars(description: string | null): number 
    {
        const currentLength = description ? description.length : 0;
        return this.DESCRIPTION_MAX_LENGTH - currentLength;
    }

    validateAndProceed(): void {
        let isValid = true;

        if (!this.selectedCodeOption) {
            if (this.moveCode6111.length === 0) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'At least one custom move code is required when "Modify Codes" is selected.',
                    life: 3000
                });
                isValid = false;
            } else {
                for (const entry of this.moveCode6111) {
                    if (!entry.expenseCode.trim()) {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Expense Code is required for entry "${entry.id}".`,
                            life: 3000
                        });
                        break;
                    }
                    if (entry.expenseCode.trim().length > this.EXPENSE_CODE_MAX_LENGTH) {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Expense Code for entry "${entry.id}" cannot exceed ${this.EXPENSE_CODE_MAX_LENGTH} characters.`,
                            life: 3000
                        });
                        break;
                    }
                    if (!entry.category) {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Category is required for entry "${entry.id}".`,
                            life: 3000
                        });
                        break;
                    }
                    if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Description for entry "${entry.id}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                            life: 3000
                        });
                        break;
                    }
                }
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    applyRowChanges(entry: MoveCode6111): void
    {
        entry.categoryId = +entry.category!?.code;
        entry.customerMoveCodeRecID = +entry.moveCode!?.code; 
        const categoryLabel = entry.category?.label || 'Unknown';

        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${categoryLabel}". Remember to press Save to persist.`,
            life: 2500
        });
    }

    saveAndSubmit(): void
    {
        let isValid = true;

        for (const entry of this.moveCode6111) {
            if (!entry.category) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Category for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if ((entry.description?.length ?? 0) > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.category?.label}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        if (this.maintainAccountService) {
            const requests = this.moveCode6111.map(entry => {
                const basePayload: any = {
                    Category6111RecID : entry.categoryId,
                    ExpenseCode: entry.expenseCode,
                    Descr: entry.description || '',
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()!,
                    CustomerMoveCodeRecID : entry.moveCode?.code
                };

                if (!this.isCreate) {
                    basePayload.RecID = entry.recId;
                }

                return basePayload;
            });


            console.log("Data we will save:", requests);

            requests.forEach(record => {
                this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1),JSON.stringify(record)).subscribe(
                {
                    next: (response) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: `Saved record".`,
                            life: 2000
                        });

                        if (this.ref && record === requests[requests.length - 1]) {
                            this.ref.close(response);
                        }
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: `Failed to save record".`,
                            life: 3000
                        });
                    },
                    complete: () =>
                    {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Complete',
                            detail: 'All records processed.',
                            life: 2000
                        });

                        if (this.ref)
                        {
                            this.ref.close(true);
                        }
                    }
                });
            });

        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }


    getSeverity(val: boolean): string
    {
        return val ? 'success' : 'danger';
    }

    toggleIsActive(entry: any): void
    {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void
    {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }

    loadCategories(): void
    {
        const url = this.ajaxService.getBaseOfApi() + COMP_MC6111 + this.viewScopeService.getCurrentOpertaingUserId();

        if (!url || !this.maintainAccountService) {
            console.warn('API endpoint or service missing.');
            return;
        }

        this.ajaxService.getData(url).subscribe({
        next: (response) => {
            const data = response as any[];
            this.categoryOptions = data.map(item => ({
            label: item.Category,
            code:  item.Category6111RecID,
            value: item.Category
            }));
            this.filteredCategories = [...this.categoryOptions];
        },
        error: (error) => {
            console.error('Failed to load categories', error);
            this.categoryOptions = [];
            this.filteredCategories = [];
        }
        });

    }

    loadMoveCodes(): void 
    {
        const url = this.ajaxService.getBaseOfApi() + COMP_MC + this.customerRecId + "/" + this.viewScopeService.getCurrentOpertaingUserId();

        this.ajaxService.getData(url).subscribe({
            next: (response) => {
                const data = response as any[];
                this.moveCodeOptions = data.map(item => ({
                    label: item.CodeMoveType,
                    value: item.CodeMoveType,
                    code: item.RecID
                }));
                this.filteredMoveCodes = [...this.moveCodeOptions];
            },
            error: (error) => {
                console.error('Failed to load move codes', error);
                this.moveCodeOptions = [];
                this.filteredMoveCodes = [];
            }
        });
    }

    filterMoveCodes(event: any): void {
        const query = event.query;
        this.filteredMoveCodes = this.moveCodeOptions.filter(
            (code) => code.label.toLowerCase().includes(query.toLowerCase())
        );
    }



    onNewCategoryChange(entry?: any): void {
        if (this.isCreate) {
            // Create mode
            if (typeof this.newEntry.category === 'string') {
                const categoryLabel = this.newEntry.category as string;

                const matchedCategory = this.categoryOptions.find(
                    option => option.label === categoryLabel
                );

                if (matchedCategory) {
                    this.newEntry.category = matchedCategory;
                    this.newEntry.categoryId = +matchedCategory.code;
                }
            }
        } else if (entry) {
            // Edit mode
            if (typeof entry.category === 'string') {
                const categoryLabel = entry.category as string;

                const matchedCategory = this.categoryOptions.find(
                    option => option.label === categoryLabel
                );

                if (matchedCategory) {
                    entry.category = matchedCategory;
                    entry.categoryId = matchedCategory.code;
                }
            }
        }
    }

    onNewMoveCodeChange(entry?: any): void {
        if (this.isCreate) {
            // Create mode
            if (typeof this.newEntry.moveCode === 'string') {
                const moveCodeLabel = this.newEntry.moveCode as string;

                const matchedMoveCode = this.moveCodeOptions.find(
                    option => option.label === moveCodeLabel
                );

                if (matchedMoveCode) {
                    this.newEntry.moveCode = matchedMoveCode;
                    this.newEntry.customerMoveCodeRecID = +matchedMoveCode.code;
                }
            }
        } else if (entry) {
            // Edit mode
            if (typeof entry.moveCode === 'string') {
                const moveCodeLabel = entry.moveCode as string;

                const matchedMoveCode = this.moveCodeOptions.find(
                    option => option.label === moveCodeLabel
                );

                if (matchedMoveCode) {
                    entry.moveCode = matchedMoveCode;
                    entry.customerMoveCodeRecID = matchedMoveCode.code;
                }
            }
        }
    }



    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }
}